<script setup lang="ts">
import { ChevronsUpDown, Building2, Search, X } from "lucide-vue-next"
import { computed, onMounted, ref } from "vue"
import { useRouter, useRoute } from "vue-router"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"

import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar"

import { useAuthStore } from "@/stores/auth"
import { useRestaurantStore } from "@/stores/restaurant"
import { toast } from "vue-sonner"
import type { Restaurant } from "@/types"
import { useI18n } from "@/composables/useI18n"

const { t } = useI18n()

const { isMobile } = useSidebar()
const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const restaurantStore = useRestaurantStore()

const restaurants = computed(() => restaurantStore.restaurants)
const activeRestaurant = computed(() => restaurantStore.restaurant)

// Search functionality for superadmin
const searchQuery = ref("")
const isSearchOpen = ref(false)

const isSuperAdmin = computed(() => authStore.user?.role === "superadmin")

// Sprawdź czy użytkownik jest w kontekście restauracji (nie w widokach administracyjnych)
const isInRestaurantContext = computed(() => {
  const path = route.path
  // Widoki administracyjne zaczynają się od /dashboard/admin
  return !path.startsWith("/dashboard/admin")
})

const filteredRestaurants = computed(() => {
  if (!searchQuery.value) return restaurants.value
  return restaurants.value.filter(
    restaurant =>
      restaurant.name.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
      restaurant.address?.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
      restaurant.city?.toLowerCase().includes(searchQuery.value.toLowerCase())
  )
})

// Flaga do śledzenia, czy restauracja została już ustawiona ręcznie przez użytkownika
const restaurantManuallySelected = ref(false)

onMounted(async () => {
  if (authStore.isAuthenticated) {
    try {
      // Pobierz wszystkie restauracje jeśli nie są jeszcze załadowane
      if (restaurants.value.length === 0) {
        await restaurantStore.fetchAllRestaurants()
      }

      // Jeśli restauracja została już ustawiona ręcznie przez użytkownika, nie nadpisuj
      if (restaurantManuallySelected.value) {
        return
      }

      // Sprawdź ponownie czy restauracja nie została ustawiona w międzyczasie
      // (np. przez ręczny wybór lub przez initializeUserRestaurant w App.vue)
      if (restaurantStore.restaurant) {
        return
      }

      // Jeśli nie ma aktywnej restauracji, spróbuj załadować preferowaną z profilu
      // Tylko dla restaurant_owner, admin i superadmin
      // UWAGA: initializeUserRestaurant w App.vue już powinno to zrobić,
      // ale na wypadek gdyby nie zadziałało, mamy fallback tutaj
      if (restaurants.value.length > 0) {
        const allowedRoles = ["restaurant_owner", "admin", "superadmin"]
        const userRole = authStore.user?.role
        const preferredRestaurantId =
          userRole && allowedRoles.includes(userRole)
            ? authStore.user?.preferredRestaurantId
            : undefined

        if (preferredRestaurantId) {
          // Znajdź preferowaną restaurację w liście
          const preferredRestaurant = restaurants.value.find(r => r.id === preferredRestaurantId)

          if (preferredRestaurant) {
            // Sprawdź jeszcze raz przed ustawieniem (może użytkownik wybrał w międzyczasie)
            if (!restaurantStore.restaurant && !restaurantManuallySelected.value) {
              await restaurantStore.setActiveRestaurant(preferredRestaurant)
            }
          } else {
            // Jeśli preferowana restauracja nie istnieje, użyj pierwszej dostępnej
            if (!restaurantStore.restaurant && !restaurantManuallySelected.value) {
              await restaurantStore.setActiveRestaurant(restaurants.value[0]!)
            }
          }
        } else {
          // Jeśli nie ma preferowanej, użyj pierwszej dostępnej
          if (!restaurantStore.restaurant && !restaurantManuallySelected.value) {
            await restaurantStore.setActiveRestaurant(restaurants.value[0]!)
          }
        }
      }
    } catch {
      // Error fetching restaurants
    }
  }
})

const selectRestaurant = async (restaurant: Restaurant) => {
  // Sprawdź czy to rzeczywiście zmiana restauracji
  const isRestaurantChange = activeRestaurant.value?.id !== restaurant.id

  // Oznacz, że użytkownik ręcznie wybrał restaurację
  restaurantManuallySelected.value = true

  // Ustaw aktywną restaurację w store i przeładuj dane
  await restaurantStore.setActiveRestaurant(restaurant)

  // Pokaż toast o zmianie restauracji
  if (isRestaurantChange) {
    toast.success(t("common.restaurant.changed", { name: restaurant.name }))

    // Jeśli użytkownik jest w kontekście restauracji, przekieruj na "Aktywne zamówienia"
    if (isInRestaurantContext.value) {
      router.push("/dashboard/active-orders")
    }
  }

  // Zamknij wyszukiwarkę po wyborze
  isSearchOpen.value = false
  searchQuery.value = ""
}

// Removed unused toggleSearch function

const clearSearch = () => {
  searchQuery.value = ""
}

const getPackageLabel = (restaurant: Restaurant | null | undefined): string => {
  if (!restaurant) return ""
  const packageType = restaurant.packageType
  if (packageType === "SIMPLE") {
    return t("common.restaurant.packageSimple")
  }
  return t("common.restaurant.packageFull")
}
</script>

<template>
  <SidebarMenu>
    <SidebarMenuItem>
      <DropdownMenu>
        <DropdownMenuTrigger as-child>
          <SidebarMenuButton
            size="lg"
            class="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
          >
            <div
              class="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground"
            >
              <Building2 class="size-4" />
            </div>
            <div class="grid flex-1 text-left text-sm leading-tight">
              <span class="truncate font-medium text-sidebar-foreground">
                {{ activeRestaurant?.name || t("common.restaurant.selectRestaurant") }}
              </span>
              <span v-if="activeRestaurant" class="truncate text-xs text-sidebar-foreground/70">
                {{ getPackageLabel(activeRestaurant) }}
              </span>
            </div>
            <ChevronsUpDown class="ml-auto text-sidebar-foreground/60" />
          </SidebarMenuButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          class="w-[--reka-dropdown-menu-trigger-width] min-w-56 rounded-lg"
          align="start"
          :side="isMobile ? 'bottom' : 'right'"
          :side-offset="4"
        >
          <DropdownMenuLabel class="text-xs text-muted-foreground">
            {{ t("common.navigation.restaurant") }}
          </DropdownMenuLabel>

          <!-- Search functionality for superadmin -->
          <div v-if="isSuperAdmin" class="p-2 border-b border-border">
            <div class="relative">
              <Search class="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                v-model="searchQuery"
                :placeholder="t('common.search.restaurantPlaceholder')"
                class="pl-8 pr-8"
                @focus="isSearchOpen = true"
              />
              <Button
                v-if="searchQuery"
                variant="ghost"
                size="sm"
                class="absolute right-1 top-1 h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
                @click="clearSearch"
              >
                <X class="h-3 w-3" />
              </Button>
            </div>
          </div>

          <DropdownMenuItem
            v-for="(restaurant, index) in filteredRestaurants"
            :key="restaurant.id"
            class="gap-2 p-2"
            @click="selectRestaurant(restaurant)"
          >
            <div class="flex size-6 items-center justify-center rounded-sm border border-border">
              <Building2 class="size-3.5 shrink-0 text-muted-foreground" />
            </div>
            <div class="flex flex-col">
              <span class="font-medium">{{ restaurant.name }}</span>
              <span class="text-xs text-muted-foreground">
                {{ getPackageLabel(restaurant) }}
              </span>
            </div>
            <DropdownMenuShortcut class="text-muted-foreground">
              ⌘{{ index + 1 }}
            </DropdownMenuShortcut>
          </DropdownMenuItem>

          <!-- No results message -->
          <div
            v-if="isSuperAdmin && searchQuery && filteredRestaurants.length === 0"
            class="p-2 text-center text-muted-foreground text-sm"
          >
            {{ t("common.search.noRestaurantsFound") }}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  </SidebarMenu>
</template>
